import * as z from "zod/v4";
import * as types from "./jsonRpcTypes";
import { CUSTOMER_NOTES_MAX_LENGTH } from "./dietaryRequirements";

/**
 * JSON-RPC route definitions, will validated input and output types.
 *
 * The frontend can call these with:
 * ```call("/routeName", inputData).then(outputData => {```
 *
 * These are implemented as HTTP POST endpoints on the server by handleRpc()
 * in src/server/jsonRpc.ts. See server/controllers
 */

const DIETARY_LABEL_MAX_LENGTH = 100;
const DIETARY_MAX_LABELS = 50;

const dietaryLabelsParser = z.array(z.string().max(DIETARY_LABEL_MAX_LENGTH)).max(DIETARY_MAX_LABELS);
export const dietaryRequirementsInputParser = z.array(
  z.object({
    diet_types: dietaryLabelsParser.optional(),
    allergies: dietaryLabelsParser.optional(),
    name: z.string().optional(),
    notes: z.string().max(CUSTOMER_NOTES_MAX_LENGTH).optional(),
  }),
);
export type DietaryRequirementsInput = z.infer<typeof dietaryRequirementsInputParser>;

export const routes = {
  "/checkPreorderAvailability": route({
    input: z.object({
      booking_id: z.string(),
    }),
    output: z.object({
      available: z.boolean(),
      error_code: z.number().optional(),
      message: z.string().optional(),
    }),
  }),
  "/savePreorders": route({
    input: z.object({
      booking_id: z.string(),
      name: z.string(),
      email: z.string(),
      customer_notes: z.string().optional(),
      dietary_requirements: dietaryRequirementsInputParser.optional(),
      order_id: z.string().optional(),
      booking_type: z.string().optional(),
    }),
    output: z.any(), // Adjust output type as needed
  }),
  "/storeCustomerDetails": route({
    input: z.object({
      booking_id: z.string(),
      email: z.string(),
      name: z.string(),
      customer_notes: z.string().optional(),
      booking_type: z.string().optional(),
    }),
    output: z.object({
      success: z.boolean(),
    }),
  }),
  "/loadClientBasket": route({
    input: z.object({
      booking_id: z.string(),
      order_id: z.string().optional(),
      booking_type: z.string().optional(),
    }),
    output: z.any(), // Adjust output type as needed
  }),

  "/checkDeposit": route({
    input: z
      .object({
        booking_id: z.string().optional(),
        pending_booking_id: z.string().optional(),
        intent: z.string(),
        vouchers: z
          .array(
            z.object({
              code: z.string(),
              amountToRedeem: z.number(),
            }),
          )
          .optional(),
      })
      .refine((data) => Boolean(data.booking_id) || Boolean(data.pending_booking_id), {
        message: "Either booking_id or pending_booking_id must be provided",
      }),
    output: z.object({
      successful: z.boolean(),
      payment_error: z.string().nullable().optional(),
      booking: types.bookingParser.nullable(),
    }),
  }),
  "/validate-signature": route({
    input: z.object({
      signature: z.string(),
      booking_id: z.string(),
    }),
    output: z.object({
      valid: z.boolean(),
    }),
  }),

  /**
   * Audit trail for a consent decision relayed from another domain: what was
   * relayed and whether the safeguards applied or rejected it.
   *
   * Deliberately carries no identifier of any kind — not the raw token, the
   * guest, the booking or the venue — so the record can be logged as it stands.
   */
  "/analytics/consent-relay-audit": route({
    // Strict: a report carrying anything beyond these fields is refused rather
    // than trimmed, so nothing identifying can reach the log by accident.
    input: z.strictObject({
      shape: z.enum(["legacy-events", "signal-token"]),
      outcome: z.enum(["applied", "ignored-malformed", "ignored-stale", "ignored-future", "ignored-superseded"]),
      version: z.number().int().min(0).max(255).optional(),
      ageSeconds: z.number().int().min(-31536000).max(31536000).optional(),
      signals: z
        .strictObject({
          analytics_storage: z.enum(["granted", "denied"]),
          ad_storage: z.enum(["granted", "denied"]),
          ad_user_data: z.enum(["granted", "denied"]),
          ad_personalization: z.enum(["granted", "denied"]),
        })
        .optional(),
    }),
    output: z.object({
      recorded: z.boolean(),
    }),
  }),

  "/captcha-key": route({
    input: z.object({}),
    output: z.string(),
  }),

  "/analytics-ids": route({
    input: z.object({}),
    output: z.object({
      measurementId: z.string(),
      containerId: z.string(),
    }),
  }),

  "/bugsnag-key": route({
    input: z.object({}),
    output: z.object({
      apiKey: z.string(),
      releaseStage: z.string(),
    }),
  }),

  "/old-checkout-url": route({
    input: z.object({}),
    output: z.string(),
  }),

  "/api/stripe/initialise": route({
    input: z.object({
      booking_id: z.string().optional(),
      action: z.string(),
      pending_preorder_id: z.string().optional(),
      pending_booking_id: z.string().optional(),
      deposit_ids: z.array(z.string()).optional(),
      name: z.string().optional(),
      email: z.string().optional(),
      customer_notes: z.string().max(CUSTOMER_NOTES_MAX_LENGTH).optional(),
      dietary_requirements: dietaryRequirementsInputParser.optional(),
      vouchers: z
        .array(
          z.object({
            code: z.string(),
            amountToRedeem: z.number(),
          }),
        )
        .optional(),
    }),
    output: types.stripeInitialiseResponseParser,
  }),

  "/api/stripe/finalise": route({
    input: z.object({
      booking_id: z.string(),
      action: z.string(),
      payment_method_id: z.string().optional(),
      setup_intent_id: z.string().optional(),
      deposits: z.array(z.string()).optional(), // Array of deposit IDs
      pending_preorder_id: z.string().optional(),
    }),
    output: types.stripeFinaliseResponseParser,
  }),

  "/updateCustomerPreOrderItems": route({
    input: z.object({
      booking_id: z.string(),
      pendingPreorderId: z.string().nullable(),
      itemId: z.string(),
      amount: z.number(),
      menuId: z.string(),
      selectedOptions: z.record(z.string(), z.record(z.string(), z.boolean())).optional(),
      multipleNames: z.boolean().optional(),
      forNames: z.array(z.string()).optional(),
      orderingFor: z.number().optional(),
      reassignExisting: z.boolean().optional(),
      booking_type: z.string().optional(),
      pending: z.boolean().optional(),
    }),
    output: z
      .object({
        // The name-only / reassignment branch of this endpoint recalculates the
        // basket without asking Collins for a discount or payment breakdown, so
        // these are optional. Declaring them as required made `handleRpc` fail
        // validation and ship the unvalidated body instead, hiding the problem.
        totalCost: z.number().optional(),
        totalDiscount: z.number().optional(),
        requiredPaymentAmount: z.number().optional(),
        serviceChargePercentage: z.number().optional(),
        totalCostWithService: z.number().optional(),
        serviceChargeAmount: z.number().optional(),
        items: z.array(types.preOrderItemParser),
        forNames: z.array(z.string()).optional(),
        pendingPreorderId: z.string().optional(),
        // Tax fields returned by Collins for tax-exclusive venues
        tax_inclusive: z.boolean().optional(),
        tax_label: z.string().optional(),
        tax_breakdown: z.array(types.taxBreakdownEntryParser).optional(),
        total_tax: z.number().optional(),
        total_net: z.number().optional(),
        total_gross: z.number().optional(),
        requiredPaymentTax: z.number().optional(),
        requiredPaymentGross: z.number().optional(),
        requiredPaymentTaxBreakdown: z.array(types.taxBreakdownEntryParser).optional(),
      })
      .nonoptional(),
  }),

  "/removeNameItems": route({
    input: z.object({
      booking_id: z.string(),
      pendingPreorderId: z.string().nullable(),
      nameIndex: z.number(),
      forNames: z.array(z.string()),
      booking_type: z.string().optional(),
    }),
    output: z.object({
      removedItemsCount: z.number(),
      forNames: z.array(z.string()),
      // Totals recalculated after the removed person's items were dropped. They
      // must be declared here or `handleRpc` strips them from the response and
      // the basket keeps showing the pre-removal Total. The tax fields are
      // included because a tax-exclusive basket renders its Total from
      // `total_gross` rather than `totalCost`. All optional because the Collins
      // recalculation can return no payload.
      totalCost: z.number().optional(),
      totalCostWithService: z.number().optional(),
      serviceChargeAmount: z.number().optional(),
      serviceChargePercentage: z.number().optional(),
      requiredPaymentAmount: z.number().optional(),
      tax_inclusive: z.boolean().optional(),
      tax_label: z.string().optional(),
      tax_breakdown: z.array(types.taxBreakdownEntryParser).optional(),
      total_net: z.number().optional(),
      total_gross: z.number().optional(),
    }),
  }),

  "/getBooking": route({
    input: z.object({
      booking_id: z.string(),
    }),
    output: types.bookingParser,
  }),

  "/getAllBookingTypeBaskets": route({
    input: z.object({
      booking_id: z.string(),
    }),
    output: z.array(
      z.object({
        bookingType: z.string(),
        bookingTypeName: z.string().optional(),
        items: z.array(types.preOrderItemParser),
        menus: z.array(types.preorderMenuParser).optional(),
        // The cached basket stores the pre-order view of the booking, not the
        // `/getBooking` shape. Only the fields the frontend reads are declared,
        // and all are optional because older cached baskets may predate them.
        booking: z
          .object({
            type: z.string().optional(),
            additional_types: z.array(types.bookingAdditionalTypeParser).optional(),
          })
          .optional(),
        forNames: z.array(z.string()).optional(),
        email: z.string().optional(),
        name: z.string().optional(),
        serviceChargePercentage: z.number().optional(),
        serviceChargeAmount: z.number().optional(),
        totalCost: z.number().optional(),
        totalCostWithService: z.number().optional(),
        requiredPaymentAmount: z.number().optional(),
        // Tax fields (tax-exclusive venues) — preserved so the /book confirmation
        // renders the Net / tax breakdown / gross Total layout instead of
        // mislabelling the net→gross tax difference as a service charge.
        tax_inclusive: z.boolean().optional(),
        tax_label: z.string().optional(),
        tax_breakdown: z.array(types.taxBreakdownEntryParser).optional(),
        total_net: z.number().optional(),
        total_gross: z.number().optional(),
        pending: z.boolean().optional(),
      }),
    ),
  }),

  "/getCustomerPreOrder": route({
    input: z.object({
      booking_id: z.string(),
      order_id: z.string().optional(),
      serviceChargePercentage: z.number().optional(),
      pending: z.boolean().optional(),
      booking_type: z.string().optional(),
    }),
    output: types.customerPreOrderParser.optional(),
  }),

  "/getPreorderCacheStatus": route({
    input: z.object({ booking_id: z.string() }),
    output: z.object({
      active: z.boolean(),
      remainingSeconds: z.number().int().nonnegative().nullable(),
      warningRemainingSeconds: z.number().int().positive(),
    }),
  }),

  "/updatePreOrderServiceCharge": route({
    input: z.object({
      booking_id: z.string(),
      pendingPreorderId: z.string().nullable(),
      items: z.array(types.preOrderItemParser),
      booking: z.any(),
      serviceChargePercentage: z.number(),
      booking_type: z.string().optional(),
      bookingType: z.string().optional(),
      pending: z.boolean().optional(),
    }),
    output: z.object({
      totalCost: z.number(),
      totalDiscount: z.number(),
      requiredPaymentAmount: z.number(),
      serviceChargePercentage: z.number(),
      serviceChargeAmount: z.number(),
      totalCostWithService: z.number().optional(),
      items: z.array(types.preOrderItemParser),
      pendingPreorderId: z.string().optional(),
      // Tax fields returned by Collins for tax-exclusive venues
      tax_inclusive: z.boolean().optional(),
      tax_label: z.string().optional(),
      tax_breakdown: z.array(types.taxBreakdownEntryParser).optional(),
      total_tax: z.number().optional(),
      total_net: z.number().optional(),
      total_gross: z.number().optional(),
      requiredPaymentTax: z.number().optional(),
      requiredPaymentGross: z.number().optional(),
      requiredPaymentTaxBreakdown: z.array(types.taxBreakdownEntryParser).optional(),
    }),
  }),

  "/freedompay/initialise": route({
    input: z.object({
      booking_id: z.string().optional(),
      booking_type: z.string().optional(),
      action: z.string().optional(),
      name: z.string().optional(),
      email: z.string().optional(),
      customer_notes: z.string().optional(),
      paymentMethod: z.string(),
      address: z.any(),
      pending_booking_id: z.string().optional(),
      recaptchaToken: z.string().optional(),
      vouchers: z
        .array(
          z.object({
            code: z.string(),
            amountToRedeem: z.number(),
          }),
        )
        .optional(),
    }),
    output: z.any().nonoptional(),
  }),

  "/freedompay/finalise": route({
    input: z.object({
      booking_id: z.string().optional(),
      booking_type: z.string().optional(),
      action: z.string().optional(),
      PaymentKeys: z.any(),
      attributes: z.any(),
      paymentType: z.string(),
      address: z.any().optional(),
      sessionKey: z.string(),
      pending_booking_id: z.string().optional(),
      pending_preorder_id: z.string().optional(),
    }),
    output: z.any().nonoptional(),
  }),

  "/freedompay/countries": route({
    input: z.object({}),
    output: z.array(z.any()).nonoptional(),
  }),

  "/freedompay/url": route({
    input: z.object({}),
    output: z.string(),
  }),

  "/feature-sentiment/config": route({
    input: z.object({}),
    output: z.object({
      url: z.string(),
      accountToken: z.string(),
      apiHost: z.string(),
    }),
  }),

  "/rewards/config": route({
    input: z.object({}),
    output: z.object({
      /**
       * Full URL of the guest's DesignMyNight rewards page. Empty when the
       * environment has no rewards URL configured, in which case no link shows.
       */
      rewardsUrl: z.string(),
    }),
  }),

  "/stripe-public-key": route({
    input: z.object({}),
    output: z.object({
      publicKey: z.string(),
    }),
  }),

  "/getItemQuantityInBasket": route({
    input: z.object({
      booking_id: z.string(),
      itemId: z.string(),
      menuId: z.string(),
      selectedOptions: z.record(z.string(), z.record(z.string(), z.boolean())).optional(),
      pending: z.boolean().optional(),
    }),
    output: z.object({ quantity: z.number() }),
  }),

  "/getTotalItemQuantityInBasket": route({
    input: z.object({
      booking_id: z.string(),
      itemId: z.string(),
      menuId: z.string(),
      pending: z.boolean().optional(),
    }),
    output: z.object({ quantity: z.number() }),
  }),

  "/validateConfigurableOptions": route({
    input: z.object({
      booking_id: z.string(),
      itemId: z.string(),
      menuId: z.string(),
      selectedOptions: z.record(z.string(), z.record(z.string(), z.boolean())),
      pending: z.boolean().optional(),
    }),
    output: z.object({
      valid: z.boolean(),
      errors: z.array(z.string()),
    }),
  }),

  "/getBasketSummary": route({
    input: z.object({
      booking_id: z.string(),
      forNames: z.array(z.string()).optional(),
      multipleNames: z.boolean().optional(),
      pending: z.boolean().optional(),
    }),
    output: z.object({
      totalItemsCount: z.number(),
      itemsByPerson: z.record(z.string(), z.array(z.any())),
    }),
  }),

  "/updateDietaryRequirements": route({
    input: z.object({
      booking_id: z.string(),
      pending: z.boolean().optional(),
      dietaryRequirements: z.array(
        z.object({
          name: z.string(),
          diet_types: dietaryLabelsParser,
          allergies: dietaryLabelsParser,
          notes: z.string().max(CUSTOMER_NOTES_MAX_LENGTH),
        }),
      ),
    }),
    output: z.object({ success: z.boolean() }),
  }),

  "/getProcessedMenuData": route({
    input: z.object({
      booking_id: z.string(),
      menuId: z.string(),
      pending: z.boolean().optional(),
    }),
    output: z.object({
      menuSettings: z.object({
        item_multiplier: z.number().optional(),
        max_per_person: z.number().optional(),
        limit_preorders: z
          .object({ type: z.literal("items"), min: z.number().min(0), max: z.number().min(0) })
          .optional(),
        discount_amount: z.number().optional(),
        hide_prices: z.boolean().optional(),
      }),
      processedItems: z.array(z.any()),
      currencySymbol: z.string(),
      fixedPriceInfo: z.object({
        isFixedPrice: z.boolean(),
        rules: z
          .array(
            z.object({
              max_items: z.number(),
              price: z.number().optional(),
              formattedPrice: z.string().optional(),
            }),
          )
          .optional(),
      }),
      menuInfo: z.object({
        name: z.string(),
        description: z.string(),
        displayText: z.object({
          multiplierText: z.string().optional(),
          discountText: z.string().optional(),
          maxPerPersonText: z.string().optional(),
          itemLimitsText: z.string().optional(),
        }),
      }),
    }),
  }),

  "/formatCurrency": route({
    input: z.object({
      amount: z.number(),
      currencySymbol: z.string().optional(),
    }),
    output: z.object({
      formattedAmount: z.string(),
    }),
  }),

  "/validateItemAddition": route({
    input: z.object({
      booking_id: z.string(),
      menuId: z.string(),
      quantity: z.number(),
      forNameIndex: z.number().optional(),
    }),
    output: z.object({
      valid: z.boolean(),
      message: z.string().optional(),
      adjustedQuantity: z.number(),
    }),
  }),

  "/clearPreorder": route({
    input: z.object({
      booking_id: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
    }),
  }),

  "/getManageCustomerPreorderDetails": route({
    input: z.object({
      booking_id: z.string(),
      serviceChargePercentage: z.number().optional(),
    }),
    output: types.manageOrderDataParser,
  }),

  "/deleteCustomerPreorderOrder": route({
    input: z.object({
      booking_id: z.string(),
      order_id: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      message: z.string().optional(),
    }),
  }),

  "/updateServiceCharge": route({
    input: z.object({
      booking_id: z.string(),
      serviceChargePercentage: z.number(),
    }),
    output: types.manageOrderDataParser,
  }),

  "/completePreorders": route({
    input: z.object({
      booking_id: z.string(),
      paymentDetails: z.any().optional(),
      serviceChargePercentage: z.number().optional(),
    }),
    output: z.object({
      success: z.boolean(),
      paymentReceived: z
        .object({
          amount: z.number(),
        })
        .optional(),
      message: z.string().optional(),
    }),
  }),

  "/fetchPendingBookingId": route({
    input: z.object({
      venue_id: z.string(),
      source: z.string().default("partner"),
      type: z.string().optional(),
      num_people: z.number().optional(),
      date: z.string().optional(),
      time: z.string().optional(),
      duration: z.number().optional(),
      affiliate_id: z.string().optional(),
      offer: types.offerIdParser.optional(),
      locale: z.string().optional(),
    }),
    output: z.object({
      pending_booking_id: z.string().nullable(),
      user: z
        .object({
          first_name: z.string(),
          last_name: z.string(),
          email: z.string(),
          phone: z.string().nullish(),
          dob: z.string().nullish(),
          /** Reward points balance. Omitted by DMN's /book handler today, so usually absent here. */
          points_balance: z.number().optional(),
          custom_details: types.customDetailsParser,
        })
        .nullable(),
      affiliate: z
        .object({
          _id: z.string(),
          name: z.string(),
          data_permission_text: z.string().optional(),
          hide_login_section: z.boolean().optional(),
          photo: z.string().optional(),
        })
        .nullable()
        .optional(),
    }),
  }),
  "/checkVoucherRedeemable": route({
    input: z.object({
      date: z.string(),
      venue: z.string(),
      voucher: z.string(),
    }),
    output: z.object({
      redeemable: z.boolean(),
      // Amount the voucher can redeem, in pence (minor currency units).
      amountToRedeem: z.number(),
      code: z.string(),
      message: z.string().optional(),
    }),
  }),
  "/createBooking": route({
    input: types.bookingCreateRequestParser,
    output: types.bookingCreateResponseParser,
  }),
  "/verifyPendingBooking": route({
    input: z.object({
      pending_booking_id: z.string(),
    }),
    output: z.object({
      valid: z.boolean(),
      message: z.string().optional(),
      /**
       * True when the failure is transient (network/timeout/5xx) and the guest
       * may safely retry, as opposed to a genuine session expiry which is terminal.
       */
      retryable: z.boolean().optional(),
    }),
  }),
  "/submitBooking": route({
    input: types.bookingSubmitRequestParser,
    output: types.bookingSubmitResponseParser,
  }),
  "/updateBookingCustomer": route({
    input: z.object({
      pending_booking_id: z.string(),
      email: z.email(),
      first_name: z.string(),
      last_name: z.string(),
      phone: z.string(),
      notes: z.string().optional(),
      dob: z.string().optional(),
      policy_confirm: z.boolean().optional(),
      newsletter_signup: z.boolean().optional(),
      partner_data_permission: z.boolean().optional(),
      marketing_preferences: z.record(z.string(), z.boolean()).optional(),
      tags: z
        .union([
          z.array(
            z.object({
              id: z.string(),
              value: z.union([z.string(), z.array(z.string()), z.boolean()]).nullable(),
              label: z.string(),
              type: z.string(),
            }),
          ),
          z.record(z.string(), z.unknown()),
        ])
        .optional(),
      group_members: z
        .array(z.looseObject({ first_name: z.string().optional(), last_name: z.string().optional() }))
        .optional(),
    }),
    output: z.object({
      success: z.boolean(),
    }),
  }),
  /**
   * Binds the partner `return_url` to the guest's session before they are sent
   * to pay, so `/confirm` redirects only where that guest's journey began.
   */
  "/bindReturnUrl": route({
    input: z.object({
      pending_booking_id: z.string().min(1),
      return_url: types.partnerReturnUrlParser,
      return_method: types.partnerReturnMethodParser.optional(),
    }),
    output: z.object({
      success: z.boolean(),
    }),
  }),
  "/checkEmailAccount": route({
    input: z.object({ email: z.email() }),
    output: z.object({ exists: z.boolean() }),
  }),
  "/loginWithUsernamePassword": route({
    input: z.strictObject({
      username: z.string().min(1).max(254),
      password: z.string().min(1).max(1024),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  "/loginWithEmail": route({
    input: z.object({
      email: z.email(),
      password: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      user: z
        .object({
          first_name: z.string(),
          last_name: z.string(),
          email: z.string(),
          phone: z.string().nullish(),
          dob: z.string().nullish(),
          points_balance: z.number().optional(),
          custom_details: types.customDetailsParser,
        })
        .optional(),
      error: z.string().optional(),
    }),
  }),
  "/loginWithOAuthToken": route({
    input: z.object({
      access_token: z.string().min(1),
    }),
    output: z.object({
      success: z.boolean(),
      user: z
        .object({
          first_name: z.string(),
          last_name: z.string(),
          email: z.string(),
          phone: z.string().nullish(),
          dob: z.string().nullish(),
          points_balance: z.number().optional(),
          custom_details: types.customDetailsParser,
        })
        .optional(),
      error: z.string().optional(),
      /** Set to "profile_incomplete" when the social account has no DMN profile yet */
      error_code: z.string().optional(),
      /** Email from the social provider, returned when no full profile exists */
      email: z.string().optional(),
    }),
  }),
  "/signUpUser": route({
    input: z.object({
      email: z.email(),
      password: z.string().min(1),
      first_name: z.string().min(1),
      last_name: z.string().min(1),
      source: z.string().optional(),
      newsletter_signup: z.boolean().optional(),
      region: z.string().optional(),
    }),
    output: z.object({
      success: z.boolean(),
      authenticated: z.boolean().optional(),
      user: z
        .object({
          first_name: z.string(),
          last_name: z.string(),
          email: z.string(),
          phone: z.string().nullish(),
          dob: z.string().nullish(),
          /** Balance after the sign-up reward, used for the welcome bonus message. */
          points_balance: z.number().optional(),
          custom_details: types.customDetailsParser,
        })
        .optional(),
      error: z.string().optional(),
      error_code: z.string().optional(),
    }),
  }),
  "/logout": route({
    input: z.object({}),
    output: z.object({
      success: z.boolean(),
    }),
  }),
  "/auth-components/config": route({
    input: z.object({}),
    output: z.object({
      version: z.string(),
      authApiUrl: z.string(),
      clientId: z.string(),
      clientSecret: z.string(),
      releaseStage: z.string(),
      scriptUrl: z.string().optional(),
    }),
  }),
  "/fetchVenueList": route({
    input: z.object({
      // IDs are joined into the Collins URL path, so restrict them to safe
      // characters to prevent path/query manipulation.
      venue_ids: z.array(z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/)).min(1),
      source: z.string().default("partner"),
    }),
    output: z.array(
      z.object({
        _id: z.string(),
        title: z.string(),
        street: z.string().optional(),
        city: z.string().optional(),
        postcode: z.string().optional(),
      }),
    ),
  }),
};

export type JsonRpcRoute<IN, OUT> = {
  input: z.ZodType<IN>;
  output: z.ZodType<OUT>;
};

function route<IN, OUT>({
  input,
  output,
}: {
  input: z.ZodType<IN>;
  output: z.ZodType<OUT>;
}): JsonRpcRoute<IN, OUT> {
  return { input, output };
}

/**
 * Thrown by `call()` for any non-200 response, carrying the status and the
 * message the server put in the response body so callers can show it.
 */
export class RpcError extends Error {
  public readonly status: number;
  public readonly bodyMessage?: string;

  constructor(status: number, message: string, bodyMessage?: string) {
    super(message);
    this.name = "RpcError";
    this.status = status;
    this.bodyMessage = bodyMessage;
    Object.setPrototypeOf(this, RpcError.prototype);
  }
}

/** Thrown by `call()` when the request outlives `RpcCallOptions.timeoutMs`; the request is aborted. */
export class RpcTimeoutError extends RpcError {
  public readonly timeoutMs: number;
  public readonly route: string;

  constructor(route: string, timeoutMs: number) {
    super(0, `API request to ${route} timed out after ${timeoutMs}ms`);
    this.name = "RpcTimeoutError";
    this.timeoutMs = timeoutMs;
    this.route = route;
    Object.setPrototypeOf(this, RpcTimeoutError.prototype);
  }
}

/**
 * Thrown by `call()` when the caller's `RpcCallOptions.signal` aborts the request.
 * The guest moved on, so callers should drop the result without showing an error.
 */
export class RpcAbortError extends Error {
  constructor(route: string) {
    super(`API request to ${route} was aborted`);
    this.name = "RpcAbortError";
    Object.setPrototypeOf(this, RpcAbortError.prototype);
  }
}

/** Optional per-call timing and cancellation. Callers that pass nothing get the original behaviour. */
export interface RpcCallOptions {
  /** Aborts the request when the caller no longer needs it, rejecting with `RpcAbortError`. */
  readonly signal?: AbortSignal;
  /** Overall limit, including reading the response; the request is aborted and `RpcTimeoutError` thrown. */
  readonly timeoutMs?: number;
  /** Fires `onSlow` once if the request is still running after this many milliseconds. */
  readonly slowAfterMs?: number;
  readonly onSlow?: () => void;
}

interface RequestLifetime {
  readonly signal: AbortSignal;
  readonly outcome: () => "timeout" | "aborted" | null;
  readonly dispose: () => void;
}

/** Combines the caller's signal with the timeout by hand: `AbortSignal.any` needs Safari 17.4+. */
function startRequestLifetime(options: RpcCallOptions): RequestLifetime {
  const { signal, timeoutMs, slowAfterMs, onSlow } = options;
  const controller = new AbortController();
  let outcome: "timeout" | "aborted" | null = null;
  const stop = (reason: "timeout" | "aborted") => {
    outcome ??= reason;
    controller.abort();
  };
  const onCallerAbort = () => stop("aborted");

  if (signal?.aborted) {
    stop("aborted");
  } else {
    signal?.addEventListener("abort", onCallerAbort, { once: true });
  }
  const timeoutId = timeoutMs === undefined ? undefined : setTimeout(() => stop("timeout"), timeoutMs);
  const slowId = slowAfterMs === undefined || !onSlow ? undefined : setTimeout(onSlow, slowAfterMs);

  return {
    signal: controller.signal,
    outcome: () => outcome,
    dispose: () => {
      clearTimeout(timeoutId);
      clearTimeout(slowId);
      signal?.removeEventListener("abort", onCallerAbort);
    },
  };
}

export type RpcRouteName = keyof typeof routes;
export type RpcInput<name extends RpcRouteName> = z.infer<(typeof routes)[name]["input"]>;
export type RpcOutput<name extends RpcRouteName> = z.infer<(typeof routes)[name]["output"]>;

async function readErrorMessage(response: Response): Promise<string | undefined> {
  try {
    const body: unknown = await response.json();
    const error = (body as { error?: unknown } | null)?.error;
    return typeof error === "string" && error.length > 0 ? error : undefined;
  } catch {
    return undefined;
  }
}

let _baseUrl = "";

/**
 * Set the base URL for all JSON-RPC calls. When the MFE is embedded on a
 * third-party site, this must point to the Guest Checkout server origin
 * (e.g. "https://book.designmynight.com") so requests are not sent to the
 * host page's domain.
 */
export function getBaseUrl(): string {
  return _baseUrl;
}

export function setBaseUrl(url: string) {
  if (!url) {
    _baseUrl = "";
    return;
  }
  const parsed = new URL(url);
  _baseUrl = parsed.origin;
}

const PREORDER_CLIENT_ID_STORAGE_KEY = "gc-preorder-client-id";
const PREORDER_CLIENT_ID_HEADER = "x-preorder-id";

interface PreorderStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

type PreorderWindow = {
  location: { search: string; origin?: string };
  localStorage?: PreorderStorage;
};

function preorderWindow(): PreorderWindow | null {
  if (typeof globalThis !== "undefined" && typeof (globalThis as { window?: PreorderWindow }).window !== "undefined") {
    return (globalThis as { window?: PreorderWindow }).window ?? null;
  }
  return null;
}

const CLIENT_PREORDER_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

/**
 * Generates a cryptographically random client preorder token.
 *
 * The token is a bearer-style basket identifier, so a predictable value is not
 * acceptable: when no secure RNG is available (e.g. a very old embedded
 * webview) this returns null and no `x-preorder-id` header is sent, letting the
 * server fall back to the Express session.
 */
function generateClientPreorderId(): string | null {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === "function") {
    return webCrypto.randomUUID();
  }
  if (typeof webCrypto?.getRandomValues === "function") {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return null;
}

let inMemoryClientPreorderId: string | null = null;

/**
 * Returns a stable per-browser identifier used to key preorder baskets.
 *
 * The embedded MFEs call the server cross-origin without credentials, so the
 * `checkoutSession` cookie never round-trips and each request would otherwise
 * receive a fresh Express session. The token is persisted in localStorage and
 * sent as the `x-preorder-id` header; for cross-origin calls where storage is
 * unavailable (e.g. a sandboxed embed), a per-page-load token is used instead.
 * Same-origin calls omit an unpersisted token so the server session remains the
 * stable basket identifier. When Web Crypto is unavailable no token is minted.
 */
export function getClientPreorderId(allowUnpersisted = false): string | null {
  const win = preorderWindow();
  if (!win) {
    return null;
  }
  try {
    const storage = win.localStorage;
    if (!storage) {
      if (!allowUnpersisted) return null;
      inMemoryClientPreorderId ??= generateClientPreorderId();
      return inMemoryClientPreorderId;
    }
    const existing = storage.getItem(PREORDER_CLIENT_ID_STORAGE_KEY);
    if (existing && CLIENT_PREORDER_ID_PATTERN.test(existing)) {
      return existing;
    }
    const created = generateClientPreorderId();
    // Never persist a null token — a missing secure RNG must not poison
    // storage for subsequent calls once one becomes available.
    if (created === null) {
      return null;
    }
    storage.setItem(PREORDER_CLIENT_ID_STORAGE_KEY, created);
    return created;
  } catch {
    if (!allowUnpersisted) return null;
    inMemoryClientPreorderId ??= generateClientPreorderId();
    return inMemoryClientPreorderId;
  }
}

export async function call<name extends keyof typeof routes>(
  name: name,
  inputData: z.infer<(typeof routes)[name]["input"]>,
  extraHeaders?: Record<string, string>,
  options?: RpcCallOptions,
): Promise<z.infer<(typeof routes)[name]["output"]>> {
  const route = routes[name];
  // Get signature from URL if present
  let signature = null;
  const win = preorderWindow();
  if (win) {
    const params = new URLSearchParams(win.location.search);
    signature = params.get("signature");
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    // use a custom http header for CSRF protection
    // see http://seclab.stanford.edu/websec/csrf/csrf.pdf
    "x-csrf": "1",
    Accept: "application/json",
    ...extraHeaders,
  };
  if (signature) {
    headers["x-signature"] = signature;
  }
  const isCrossOrigin = Boolean(win?.location.origin && _baseUrl && _baseUrl !== win.location.origin);
  const clientPreorderId = getClientPreorderId(isCrossOrigin);
  if (clientPreorderId && !Object.keys(headers).some((h) => h.toLowerCase() === PREORDER_CLIENT_ID_HEADER)) {
    headers[PREORDER_CLIENT_ID_HEADER] = clientPreorderId;
  }
  const body = JSON.stringify(route.input.parse(inputData));
  const lifetime = options ? startRequestLifetime(options) : undefined;
  try {
    if (lifetime?.signal.aborted) throw new RpcAbortError(name);
    const r = await fetch(`${_baseUrl}${name}`, {
      method: "post",
      body,
      headers,
      ...(lifetime ? { signal: lifetime.signal } : {}),
    });
    if (r.status === 200) {
      // note we don't validate with route.output.parse here. we
      // assume the server is sending valid data.
      return await r.json().then((data: any) => data.result);
    } else {
      const bodyMessage = await readErrorMessage(r);
      throw new RpcError(r.status, `API error accessing ${_baseUrl}${name}: ${r.status}`, bodyMessage);
    }
  } catch (err) {
    const outcome = lifetime?.outcome();
    if (outcome === "timeout") throw new RpcTimeoutError(name, options?.timeoutMs ?? 0);
    if (outcome === "aborted") throw new RpcAbortError(name);
    throw err;
  } finally {
    lifetime?.dispose();
  }
}
